/**
 * The orbit around your profile photo: each correspondent's avatar sits as
 * close to yours as the relationship has earned. Layout is `layoutOrbit`;
 * this only draws it. Contacts start on the outer edge and glide in to their
 * place, and the whole system turns slowly (both off under reduced motion).
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { TIER_LABELS, type RelationshipTier } from '@toiyp/shared';
import type { RelationshipWithPeer } from '../relationships/api';
import { Avatar } from './avatar';
import { TIER_RADIUS, layoutOrbit } from './orbit';

/** The faint guide rings, drawn through the inner, middle and outer bands. */
const RINGS = [TIER_RADIUS.trusted, TIER_RADIUS.tepid, TIER_RADIUS.new];

/** Closer contacts draw a little larger, as a share of the stage width. */
const SIZE: Record<RelationshipTier, number> = {
  trusted: 16,
  hot: 15,
  tepid: 14,
  new: 13,
  cold: 12,
};

export function ProfileOrbit({
  relationships,
  children,
}: {
  relationships: RelationshipWithPeer[];
  /** Your own photo, drawn at the centre. */
  children: ReactNode;
}) {
  const { orbiters, overflow } = layoutOrbit(relationships);
  // First paint puts everyone on the rim; the next frame lets them drift in.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setSettled(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const summary =
    orbiters.length === 0
      ? 'Your orbit is empty for now'
      : `Your orbit: ${orbiters.map((o) => `${o.name}, ${TIER_LABELS[o.tier]}`).join('; ')}`;

  return (
    <figure className="orbit" aria-label={summary}>
      <div className="orbit__stage">
        <svg className="orbit__rings" viewBox="0 0 100 100" aria-hidden="true">
          {RINGS.map((r) => (
            <circle key={r} cx="50" cy="50" r={r * 50} />
          ))}
        </svg>
        <div className="orbit__spin" aria-hidden="true">
          {orbiters.map((o) => {
            const rad = (o.angle * Math.PI) / 180;
            const radius = settled ? o.radius : 1;
            const style = {
              left: `${50 + Math.sin(rad) * radius * 50}%`,
              top: `${50 - Math.cos(rad) * radius * 50}%`,
              '--orbit-size': `${SIZE[o.tier]}%`,
            } as CSSProperties;
            return (
              <span
                key={o.id}
                className={`orbit__body orbit__body--${o.tier}${o.hidden ? ' orbit__body--hidden' : ''}`}
                style={style}
                title={`${o.name} · ${TIER_LABELS[o.tier]}`}
              >
                <Avatar name={o.name} avatar={o.peer.avatar} />
              </span>
            );
          })}
        </div>
        <div className="orbit__centre">{children}</div>
      </div>
      <figcaption className="orbit__caption">
        {orbiters.length === 0
          ? 'Your orbit fills in as you write to people.'
          : 'The closer someone sits, the more your letters have built.'}
        {overflow > 0 && <span className="orbit__more"> +{overflow} further out</span>}
      </figcaption>
    </figure>
  );
}
